import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { Subject, forkJoin, takeUntil } from 'rxjs'
import { GeomapDomainService } from '../../domain/geomap.domain'
import { CoordinateDto, PolygonDto, RaccordementPublic, Scenario, TrenchTableRow, Zone } from '../../model/zone-measures'
import { SunleonService } from '../../../sunleon/data-access/http/sunleon.service'
import { SessionZoneSaveDto } from '../../../sunleon/data-access/http/dto/session-zone-save-dto'
import { ParametreService } from 'src/app/parametre/data-access/http/parametre.service'
import { ParametreTableauSaveDto } from 'src/app/parametre/data-access/http/dto/parametre-tableau-save-dto'
import { ParametreSpecifiqueSaveDto } from 'src/app/parametre/data-access/http/dto/parametre-specifique-save-dto'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'

@Component({
  selector: 'app-form-raccordement',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './form-raccordement.component.html',
  styleUrls: ['./form-raccordement.component.scss'],
})
export class FormRaccordementComponent implements OnInit {
  @Input() parentForm: FormGroup | null = null
  @Input() parametresSimple: Map<string, string> | null = null
  @Input() parametresTableau: Map<string, ParametreTableauSaveDto> | null = null
  @Input() parametresSpecifique: Map<string, ParametreSpecifiqueSaveDto> | null = null

  @Output()
  formReady: EventEmitter<FormGroup> = new EventEmitter<FormGroup>()

  raccordementForm: FormGroup
  trenchRows: TrenchTableRow[] = []
  selectedRaccordementType: 'BT' | 'HT' = 'BT'
  pendingZoneIds: number[] = []
  pendingTrenchIsPdl = false
  currentCounterCount = 0
  currentTrenchSelectionMode: 'private' | 'public' | 'ptr' | null = null
  activeTrenchLabel: string | null = null
  isCalculatingPower = false
  seuilpostehtbt = 0
  coefConversionKVAKWC = 0
  nombreCentraleBtMax: number | null = null
  messageNombreCentraleBtMax: string | null = null
  powerCalculationErrorMessage: string | null = null

  private readonly geomapDomainService = inject(GeomapDomainService)
  private readonly formBuilder = inject(FormBuilder)
  private readonly sunleonService = inject(SunleonService)
  private readonly unsubscribe$ = new Subject<void>()

  constructor(private parametreService: ParametreService, private modalService: NgbModal) {
    this.raccordementForm = this.formBuilder.group({
      puissanceCentraleEstimee: [null],
      typeRaccordement: ['BT'],
      nombreCompteur: [null],
      distanceVoirieEnedis: [null],
      distanceTrancheeEnrobe1: [null],
      distanceTrancheeTerrainNaturelle1: [null],
      distanceTrancheePassagedecable1: [null],
      distanceTrancheeEnrobe2: [null],
      distanceTrancheeTerrainNaturelle2: [null],
      distanceTrancheePassagedecable2: [null],
      distanceTrancheeEnrobe3: [null],
      distanceTrancheeTerrainNaturelle3: [null],
      distanceTrancheePassagedecable3: [null],
    })
  }

  ngOnInit(): void {
    this.geomapDomainService.openRaccordementTools()
    this.loadCompteurParameters()

    this.raccordementForm
      .get('typeRaccordement')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.selectedRaccordementType = value ?? 'BT'
        this.powerCalculationErrorMessage = null
        this.geomapDomainService.setSelectedRaccordementType(this.selectedRaccordementType)
        this.parentForm?.get('modevalorisation')?.get('ptrAC')?.setValue(this.selectedRaccordementType === 'HT', { emitEvent: false })
      })

    this.raccordementForm
      .get('nombreCompteur')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.applyCounterPlacementLimit(Number(value ?? 0))
      })

    this.parentForm
      ?.get('modevalorisation')
      ?.get('modeValorisation')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.powerCalculationErrorMessage = null
        const requestedCount = Number(this.raccordementForm.get('nombreCompteur')?.value ?? 0)
        this.applyCounterPlacementLimit(requestedCount)
      })

    this.geomapDomainService
      .getDistance$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((raccordement: RaccordementPublic) => {
        this.raccordementForm.patchValue(
          {
            distanceVoirieEnedis: raccordement.distanceVoirieEnedis === 0 ? null : raccordement.distanceVoirieEnedis,
            distanceTrancheeEnrobe1: raccordement.distanceTrancheeEnrobe1 === 0 ? null : raccordement.distanceTrancheeEnrobe1,
            distanceTrancheeTerrainNaturelle1:
              raccordement.distanceTrancheeTerrainNaturelle1 === 0 ? null : raccordement.distanceTrancheeTerrainNaturelle1,
            distanceTrancheePassagedecable1:
              raccordement.distanceTrancheePassagedecable1 === 0 ? null : raccordement.distanceTrancheePassagedecable1,
            distanceTrancheeEnrobe2: raccordement.distanceTrancheeEnrobe2 === 0 ? null : raccordement.distanceTrancheeEnrobe2,
            distanceTrancheeTerrainNaturelle2:
              raccordement.distanceTrancheeTerrainNaturelle2 === 0 ? null : raccordement.distanceTrancheeTerrainNaturelle2,
            distanceTrancheePassagedecable2:
              raccordement.distanceTrancheePassagedecable2 === 0 ? null : raccordement.distanceTrancheePassagedecable2,
            distanceTrancheeEnrobe3: raccordement.distanceTrancheeEnrobe3 === 0 ? null : raccordement.distanceTrancheeEnrobe3,
            distanceTrancheeTerrainNaturelle3:
              raccordement.distanceTrancheeTerrainNaturelle3 === 0 ? null : raccordement.distanceTrancheeTerrainNaturelle3,
            distanceTrancheePassagedecable3:
              raccordement.distanceTrancheePassagedecable3 === 0 ? null : raccordement.distanceTrancheePassagedecable3,
          },
          { emitEvent: false }
        )
      })

    this.geomapDomainService
      .getTrenchTableRows$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((rows: TrenchTableRow[]) => {
        this.trenchRows = rows
      })

    this.geomapDomainService.pendingTrenchZoneIds$.pipe(takeUntil(this.unsubscribe$)).subscribe((zoneIds: number[]) => {
      this.pendingZoneIds = zoneIds
    })

    this.geomapDomainService.pendingTrenchIsPdl$.pipe(takeUntil(this.unsubscribe$)).subscribe((isPdl: boolean) => {
      this.pendingTrenchIsPdl = isPdl
    })

    this.geomapDomainService.trenchSelectionMode$.pipe(takeUntil(this.unsubscribe$)).subscribe((mode) => {
      this.currentTrenchSelectionMode = mode
    })

    this.geomapDomainService.activeTrenchLabel$.pipe(takeUntil(this.unsubscribe$)).subscribe((label) => {
      this.activeTrenchLabel = label
    })

    this.geomapDomainService.getCounterCount$().pipe(takeUntil(this.unsubscribe$)).subscribe((count: number) => {
      this.currentCounterCount = count
    })

    this.formReady.emit(this.raccordementForm)
  }

  selectRaccordementType(type: 'BT' | 'HT'): void {
    this.raccordementForm.get('typeRaccordement')?.setValue(type)
  }

  calculatePuissanceTotaleKwc(): void {
    const modeValorisation = this.parentForm?.get('modevalorisation')?.get('modeValorisation')?.value
    if (!modeValorisation) {
      this.powerCalculationErrorMessage = 'Indiquer le mode de valorisation et compléter correctement chaque zone'
      return
    }

    const scenario = this.buildScenarioForPower()
    if (!scenario.zones || scenario.zones.length === 0) {
      this.powerCalculationErrorMessage = 'Indiquer le mode de valorisation et compléter correctement chaque zone'
      return
    }

    this.powerCalculationErrorMessage = null
    this.isCalculatingPower = true
    forkJoin({
      coefficient: this.parametreService.getParametreByCode('COEFF_CONVERSION_KWA_KWC'),
      seuil: this.parametreService.getParametreByCode('SEUIL_POSTE_HTBT'),
      puissanceTotaleKwc: this.sunleonService.getPuissanceTotaleKwc(scenario),
    }).subscribe({
      next: ({ coefficient, seuil, puissanceTotaleKwc }) => {
        this.coefConversionKVAKWC = Number(coefficient.valeur)
        this.seuilpostehtbt = Number(seuil.valeur)
        const roundedPower = this.roundToTwoDecimals(puissanceTotaleKwc ?? 0)
        const counterCount =
          roundedPower > 0 && this.coefConversionKVAKWC > 0 && this.seuilpostehtbt > 0
            ? Math.ceil((roundedPower / this.coefConversionKVAKWC) / this.seuilpostehtbt)
            : null
        this.raccordementForm.patchValue({
          puissanceCentraleEstimee: roundedPower === 0 ? null : roundedPower,
          nombreCompteur: counterCount,
        })
        this.applyCounterPlacementLimit(Number(counterCount ?? 0))
        this.isCalculatingPower = false
      },
      error: () => {
        this.powerCalculationErrorMessage = null
        this.isCalculatingPower = false
      },
    })
  }

  startCounterPlacement(): void {
    const requestedCount = Number(this.raccordementForm.get('nombreCompteur')?.value ?? 0)
    this.geomapDomainService.startCounterPlacement(requestedCount)
  }

  startPrivateTrench(): void {
    this.geomapDomainService.startPrivateTrenchSelection()
  }

  startPublicTrench(): void {
    if (!this.isPublicTrenchAllowed()) {
      return
    }
    this.geomapDomainService.startPublicTrenchSelection()
  }

  confirmZoneSelection(): void {
    this.geomapDomainService.confirmPendingTrenchSelection()
  }

  togglePrivateZoneSelection(zoneId: number): void {
    this.geomapDomainService.togglePendingTrenchZoneFromButton(zoneId)
  }

  togglePrivatePdlSelection(): void {
    this.geomapDomainService.togglePendingTrenchPdl()
  }

  editRow(label: string): void {
    this.geomapDomainService.selectTrenchRow(label)
  }

  deleteRow(label: string): void {
    this.geomapDomainService.deleteTrenchRow(label)
  }

  editTrenchValue(label: string, field: 'enrobee' | 'terrainNaturel' | 'passageToiture', event: Event): void {
    const input = event.target as HTMLInputElement
    const nextValue = Number(input.value || 0)
    if (!this.geomapDomainService.hasTrenchDrawings(label, field)) {
      this.geomapDomainService.updateManualTrenchRow(label, field, nextValue)
      return
    }

    input.blur()
    const modalRef = this.modalService.open(ConfirmationModalComponent)
    modalRef.componentInstance.message = `La saisie manuelle d'une tranchée entrainera la suppression des tracés dessinés précédemment.`
    modalRef.componentInstance.confirmation = 'Voulez vous continuer ?'
    modalRef.result.then((confirmed: boolean) => {
      if (!confirmed) {
        input.value = this.getDisplayedTrenchValue(label, field)
        return
      }
      this.geomapDomainService.clearTrenchDrawings(label, field)
      this.geomapDomainService.updateManualTrenchRow(label, field, nextValue)
    })
  }

  canAddCounter(): boolean {
    const requestedCount = Number(this.raccordementForm.get('nombreCompteur')?.value ?? 0)
    if (this.isAcMode()) {
      return true
    }
    return requestedCount > 0 && this.currentCounterCount < requestedCount
  }

  formatDistanceMl(value: number | null | undefined): string {
    const safeValue = Number(value ?? 0)
    if (safeValue === 0) {
      return ''
    }
    return `${safeValue.toFixed(2)} ml`
  }

  private getDisplayedTrenchValue(label: string, field: 'enrobee' | 'terrainNaturel' | 'passageToiture'): string {
    const row = this.trenchRows.find((item) => item.zoneLabel === label)
    const value = row?.[field]
    return value == null || Number(value) === 0 ? '' : String(value)
  }

  isPrivateSelectionVisible(): boolean {
    return this.currentTrenchSelectionMode === 'private'
  }

  isActionActive(type: 'private' | 'public' | 'ptr'): boolean {
    if (type === 'private') {
      return (
        this.currentTrenchSelectionMode === 'private' ||
        (!!this.activeTrenchLabel && this.activeTrenchLabel !== 'Public')
      )
    }
    if (type === 'public') {
      return this.activeTrenchLabel === 'Public'
    }
    return (
      this.activeTrenchLabel === 'PDL' ||
      (this.currentTrenchSelectionMode === 'private' && this.pendingTrenchIsPdl)
    )
  }

  getPrivateButtonLabel(): string {
    if (this.currentTrenchSelectionMode === 'private' && this.pendingTrenchIsPdl) {
      return 'Tranchée privée : PDL'
    }
    if (this.currentTrenchSelectionMode === 'private' && this.pendingZoneIds.length > 0) {
      return `Tranchée privée : ${this.pendingZoneIds.join(' & ')}`
    }
    if (this.activeTrenchLabel && this.activeTrenchLabel !== 'Public') {
      return `Tranchée privée : ${this.activeTrenchLabel}`
    }
    return 'Ajouter une tranchée privée'
  }

  getPendingZoneSelectionLabel(): string {
    if (this.pendingTrenchIsPdl) {
      return 'Compteur selectionne : PDL'
    }
    if (this.pendingZoneIds.length === 0) {
      return 'Selectionnez sur la carte soit un compteur, soit une ou plusieurs zones.'
    }
    return `Zones selectionnées : ${this.pendingZoneIds.join(' & ')}`
  }

  canSelectPrivateZone(zoneId: number): boolean {
    return !!this.parentForm?.get(`zone${zoneId}`)?.get(`typePoseZone${zoneId}`)?.value
  }

  shouldShowBtCounterFields(): boolean {
    return this.selectedRaccordementType === 'BT' && !this.isAcMode()
  }

  shouldShowCounterWarning(): boolean {
    const counterCount = Number(this.raccordementForm.get('nombreCompteur')?.value ?? 0)
    return !this.isAcMode() && this.selectedRaccordementType === 'BT' && this.nombreCentraleBtMax !== null && counterCount > this.nombreCentraleBtMax
  }

  isPublicTrenchAllowed(): boolean {
    return this.parentForm?.get('modevalorisation')?.get('modeValorisation')?.value !== 'AC'
  }

  private buildScenarioForPower(): Scenario {
    const zones = this.buildZonesForPower()
    const projet = this.parentForm?.get('projet')?.value
    const modeValorisation = this.parentForm?.get('modevalorisation')?.value

    return {
      commun: {
        typeGammePanneau: this.parentForm?.get('capexgeneralises')?.get('typeGammePanneau')?.value ?? null,
        modeValorisation: modeValorisation?.modeValorisation ?? null,
        puissanceSouscriteKVA: modeValorisation?.puissanceSouscriteKVA ?? null,
        modeRaccordement: this.raccordementForm.get('typeRaccordement')?.value ?? null,
        mecanismeReventeSurplus: modeValorisation?.mecanismeReventeSurplus ?? null,
      },
      zones: zones.map((zone) => this.mapSessionZoneToScenarioZone(zone)),
      centrales: [],
      parametres: Object.fromEntries(this.parametresSimple ?? new Map<string, string>()),
      parametresTableau: Object.fromEntries(this.parametresTableau ?? new Map<string, ParametreTableauSaveDto>()),
      parametresSpecifique: Object.fromEntries(this.parametresSpecifique ?? new Map<string, ParametreSpecifiqueSaveDto>()),
    }
  }

  private buildZonesForPower(): SessionZoneSaveDto[] {
    const zones: SessionZoneSaveDto[] = []
    const zone1 = this.parentForm?.get('zone1')?.value
    const zone2 = this.parentForm?.get('zone2')?.value
    const zone3 = this.parentForm?.get('zone3')?.value
    const zone4 = this.parentForm?.get('zone4')?.value
    const zone5 = this.parentForm?.get('zone5')?.value
    const zone6 = this.parentForm?.get('zone6')?.value

    const createZone = (id: number, values: any, fieldSuffix: string): SessionZoneSaveDto | null => {
      if (!values || !values[`typePose${fieldSuffix}`]) {
        return null
      }

      return {
        id,
        typePose: values[`typePose${fieldSuffix}`],
        surface: values[`surface${fieldSuffix}`],
        puissanceChoisie: values[`puissanceChoisie${fieldSuffix}`],
        azimut: values[`azimut${fieldSuffix}`],
        typeToiture: values[`typeToiture${fieldSuffix}`] === '' ? null : values[`typeToiture${fieldSuffix}`],
        orientation: values[`orientationTerrasse${fieldSuffix}`] ?? null,
        pvReady: values[`pvReady${fieldSuffix}`],
        inclinaison: values[`inclinaison${fieldSuffix}`],
        typeVehiculeParking:
          values[`typeVehiculeParking${fieldSuffix}`] === '' ? null : values[`typeVehiculeParking${fieldSuffix}`],
        typePlaceParking: values[`typePlaceParking${fieldSuffix}`] === '' ? null : values[`typePlaceParking${fieldSuffix}`],
        polygons: values[`polygons${fieldSuffix}`] as PolygonDto[] | null,
        azimutCoordinates: values[`azimutCoordinate${fieldSuffix}`] as CoordinateDto[] | null,
        // Panneaux calepinés : sans eux, le back retombe sur l'estimation par surface et la
        // puissance du raccordement diverge de celle de la bulle récap / de la sauvegarde.
        // L'index 3 du state est réservé à la zone ModeValorisation : les zones 4/5/6 sont
        // décalées de +1 (cf. form-parent et geomap.domain.ts). Aligné sur la sauvegarde.
        panneaux: this.geomapDomainService.getPanneauxSnapshot(id <= 3 ? id - 1 : id),
      }
    }

    const zone1Dto = createZone(1, zone1, 'Zone1')
    const zone2Dto = createZone(2, zone2, 'Zone2')
    const zone3Dto = createZone(3, zone3, 'Zone3')
    const zone4Dto = createZone(4, zone4, 'Zone4')
    const zone5Dto = createZone(5, zone5, 'Zone5')
    const zone6Dto = createZone(6, zone6, 'Zone6')

    if (zone1Dto) {
      zones.push(zone1Dto)
    }
    if (zone2Dto) {
      zones.push(zone2Dto)
    }
    if (zone3Dto) {
      zones.push(zone3Dto)
    }
    if (zone4Dto) {
      zones.push(zone4Dto)
    }
    if (zone5Dto) {
      zones.push(zone5Dto)
    }
    if (zone6Dto) {
      zones.push(zone6Dto)
    }

    return zones
  }

  private roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100
  }

  private isAcMode(): boolean {
    return this.parentForm?.get('modevalorisation')?.get('modeValorisation')?.value === 'AC'
  }

  shouldShowDistanceVoirieVrd(): boolean {
    return this.isAcMode()
  }

  private applyCounterPlacementLimit(requestedCount: number): void {
    this.geomapDomainService.setCounterPlacementLimit(this.isAcMode() ? 0 : requestedCount)
  }

  private loadCompteurParameters(): void {
    forkJoin({
      nombreCentraleBtMax: this.parametreService.getParametreByCode('NOMBRE_CENTRALE_BT_MAX'),
      messageNombreCentraleBtMax: this.parametreService.getParametreByCode('MESSAGE_NOMBRE_CENTRALE_BT_MAX'),
    })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ nombreCentraleBtMax, messageNombreCentraleBtMax }) => {
          this.nombreCentraleBtMax = Number(nombreCentraleBtMax.valeur)
          this.messageNombreCentraleBtMax = messageNombreCentraleBtMax.valeur ?? null
        },
      })
  }

  private mapSessionZoneToScenarioZone(zone: SessionZoneSaveDto): Zone {
    return {
      id: zone.id,
      typePose: zone.typePose as any,
      surface: zone.surface,
      puissanceChoisie: zone.puissanceChoisie ?? null,
      azimut: zone.azimut,
      pvReady: zone.pvReady,
      typeToiture: zone.typeToiture as any,
      inclinaison: zone.inclinaison,
      typeVehiculeParking: zone.typeVehiculeParking as any,
      typePlaceParking: zone.typePlaceParking as any,
      azimutCoordinate: zone.azimutCoordinates?.[0] ?? null,
      polygons: zone.polygons,
      exclusions: null,
      panneaux: zone.panneaux ?? null,
    }
  }
}
